/**
 * useApi — module-local convenience hook for the ApiClient + AbortController wiring.
 *
 * Track A is expected to promote this to a shared hook at
 * `apps/ROP-teams/src/hooks/useApi.ts`.  Until then each module carries its own
 * copy so Track B1 can ship without editing Track A's surface.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { ApiClient } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';

export interface UseApiHelpers {
  client: ApiClient;
  /**
   * Creates a fresh AbortController scoped to this call.  The controller's
   * signal should be passed to the api-client method via `{ signal }`; it
   * will be aborted automatically when the component unmounts.
   */
  makeSignal: () => AbortSignal;
}

/**
 * Returns the ApiClient and a signal factory whose lifetime is tied to the
 * consumer component.  Every call to `makeSignal()` creates a new
 * AbortController and registers it for cleanup; unmount triggers abort on
 * every outstanding request (Rule #7).
 */
export function useApi(): UseApiHelpers {
  const client = useApiClient();
  const controllers = useRef<Set<AbortController>>(new Set());

  useEffect(() => {
    const set = controllers.current;
    return () => {
      set.forEach((c) => {
        try { c.abort(); } catch { /* ignore */ }
      });
      set.clear();
    };
  }, []);

  const makeSignal = useCallback((): AbortSignal => {
    const c = new AbortController();
    controllers.current.add(c);
    return c.signal;
  }, []);

  return { client, makeSignal };
}
